import React, { useEffect, useState } from 'react';
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CFormInput,
  CButton,
  CTable,
  CTableHead,
  CTableHeaderCell,
  CTableBody,
  CTableRow,
  CTableDataCell,
  CBadge,
  CCardText,
} from '@coreui/react';
import { toast } from 'react-toastify';
import CookieFunctions from '../functions/CookieFunctions';

export default function TimeOffDashboard() {
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().substring(0, 10);
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Basic Team Leader filter
  const [teamLeaderFilter, setTeamLeaderFilter] = useState('');

  function fetchDashboard() {
    setLoading(true);

    const myHeaders = new Headers();
    myHeaders.append('Authorization', 'Bearer ' + CookieFunctions.getJWTToken());

    const formData = new FormData();
    formData.append('date', selectedDate);

    fetch('/api/manager/timeOffDashboard', {
      method: 'POST',
      headers: myHeaders,
      body: formData,
    })
      .then((res) => {
        if (res.ok) return res.json();
        return res.text().then((t) => {
          throw new Error(t || 'Unable to load dashboard');
        });
      })
      .then((json) => {
        setData(json || []);
      })
      .catch((err) => {
        toast.error(err.message, {
          closeButton: true,
          autoClose: true,
          position: toast.POSITION.BOTTOM_CENTER,
          theme: 'colored',
        });
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter data by Team Leader name (case-insensitive, partial match)
  const filteredData = teamLeaderFilter
    ? data.filter(
        (row) =>
          row.teamLeader &&
          row.teamLeader.toLowerCase().includes(teamLeaderFilter.toLowerCase())
      )
    : data;

  const totalFleet = filteredData.reduce((sum, x) => sum + x.fleetSize, 0);
  const totalOnLeave = filteredData.reduce((sum, x) => sum + x.driversOnLeave, 0);

  return (
    <CCard className="mb-4">
      <CCardHeader className="fs-3">Time Off Dashboard</CCardHeader>
      <CCardBody>
        <CRow className="mb-3">
          <CCol md={3} sm={12} className="my-2">
            <label className="form-label fs-5">Date</label>
            <CFormInput
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </CCol>
          <CCol md={3} sm={12} className="my-2">
            <label className="form-label fs-5">Team Leader</label>
            <CFormInput
              type="text"
              placeholder="Type to filter by Team Leader"
              value={teamLeaderFilter}
              onChange={(e) => setTeamLeaderFilter(e.target.value)}
            />
          </CCol>
          <CCol md={2} sm={12} className="d-flex align-items-end my-2">
            <CButton color="primary" onClick={fetchDashboard} disabled={loading}>
              {loading ? 'Loading...' : 'Refresh'}
            </CButton>
          </CCol>
          {filteredData && filteredData.length > 0 && (
            <CCol md={4} sm={12} className="my-2">
              <CCardText className="fs-5">
                Overall for <b>{selectedDate}</b>:
              </CCardText>
              <CCardText className="fs-6">
                Total fleet size: <b>{totalFleet}</b> | Drivers on leave: <b>{totalOnLeave}</b>
              </CCardText>
            </CCol>
          )}
        </CRow>

        <CTable responsive hover>
          <CTableHead>
            <CTableRow>
              <CTableHeaderCell>Team Leader</CTableHeaderCell>
              <CTableHeaderCell>Fleet Size</CTableHeaderCell>
              <CTableHeaderCell>Threshold (%)</CTableHeaderCell>
              <CTableHeaderCell>Allowed / Day</CTableHeaderCell>
              <CTableHeaderCell>On Leave</CTableHeaderCell>
              <CTableHeaderCell>Remaining</CTableHeaderCell>
              <CTableHeaderCell>Status</CTableHeaderCell>
            </CTableRow>
          </CTableHead>
          <CTableBody>
            {filteredData && filteredData.length > 0 ? (
              filteredData.map((row, idx) => {
                const remaining = row.allowedPerDay - row.driversOnLeave;

                let statusLabel = '';
                let statusColor = '';
                if (row.driversOnLeave < row.allowedPerDay) {
                  statusLabel = 'Below Threshold';
                  statusColor = 'success';
                } else if (row.driversOnLeave === row.allowedPerDay) {
                  statusLabel = 'Threshold Met';
                  statusColor = 'warning';
                } else {
                  statusLabel = 'Above Threshold';
                  statusColor = 'danger';
                }

                const remainingText = remaining > 0 ? remaining : 0;

                return (
                  <CTableRow key={idx}>
                    <CTableDataCell>{row.teamLeader}</CTableDataCell>
                    <CTableDataCell>{row.fleetSize}</CTableDataCell>
                    <CTableDataCell>{row.thresholdPercent.toFixed(2)}</CTableDataCell>
                    <CTableDataCell>{row.allowedPerDay}</CTableDataCell>
                    <CTableDataCell>{row.driversOnLeave}</CTableDataCell>
                    <CTableDataCell>{remainingText}</CTableDataCell>
                    <CTableDataCell>
                      <CBadge color={statusColor} shape="rounded-pill">
                        {statusLabel}
                      </CBadge>
                    </CTableDataCell>
                  </CTableRow>
                );
              })
            ) : (
              <CTableRow>
                <CTableDataCell colSpan={7} className="text-center">
                  No data available for the selected date.
                </CTableDataCell>
              </CTableRow>
            )}
          </CTableBody>
        </CTable>
      </CCardBody>
    </CCard>
  );
}